import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from "@/lib/i18n";
import { Crosshair, Maximize2, Plus, Minus } from 'lucide-react';
import paper from 'paper';
import { calculateMMBounds } from '@/components/svg-editor/use-item-bounds';
import { MAX_SMALL_GLASS_SIZE, MAX_LARGE_GLASS_SIZE } from '@/constants/editor';

export default function QuickTransformPanel({
  selectedPaperObjects = [],
  onProjectChange,
  theme = 'dark',
}) {
  const { t } = useTranslation('editor');
  const isEnabled = selectedPaperObjects.length > 0;
  const [maxSize, setMaxSize] = useState('');
  const sliderRef = useRef(null);
  const repeatTimerRef = useRef(null);
  const repeatDelayRef = useRef(null);


  const getLargestItemInfo = useCallback(() => {
    if (!selectedPaperObjects.length) return { item: null, sizeMm: 0 };

    let maxVal = 0;
    let mainItem = null;

    selectedPaperObjects.forEach((pItem) => {
      const bounds = calculateMMBounds(pItem);
      if (bounds) {
        const currentMax = bounds.maxDiameterMm;
        if (currentMax > maxVal) {
          maxVal = currentMax;
          mainItem = pItem;
        }
      }
    });

    return { item: mainItem, sizeMm: maxVal };
  }, [selectedPaperObjects]);

  const { sizeMm: currentMaxSize } = getLargestItemInfo();

  const is26Active = isEnabled && Math.abs(currentMaxSize - MAX_SMALL_GLASS_SIZE) < 0.01;
  const is46Active = isEnabled && Math.abs(currentMaxSize - MAX_LARGE_GLASS_SIZE) < 0.01;

  const triggerCanvasUpdate = useCallback(() => {
    const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
    if (activeProject?.view) activeProject.view.update();

    window.dispatchEvent(new CustomEvent('paper-item-transform'));
    if (typeof onProjectChange === 'function') onProjectChange();
  }, [onProjectChange]);

  const scaleAllToTarget = useCallback((targetSizeMm) => {
    if (!isEnabled || !targetSizeMm || targetSizeMm <= 0) return;

    const { sizeMm: freshMaxSize } = getLargestItemInfo();
    if (!freshMaxSize || freshMaxSize <= 0) return;

    const scaleFactor = targetSizeMm / freshMaxSize;
    if (!Number.isFinite(scaleFactor) || Math.abs(scaleFactor - 1) < 0.00001) return;

    let targetCenter = null;
    if (selectedPaperObjects.length === 1) {
      const info = calculateMMBounds(selectedPaperObjects[0]);
      targetCenter = info?.geoCenter
        ? new paper.Point(info.geoCenter.x, info.geoCenter.y)
        : selectedPaperObjects[0].bounds.center;
    } else {
      let compoundBounds = null;
      selectedPaperObjects.forEach((pItem) => {
        compoundBounds = compoundBounds ? compoundBounds.unite(pItem.bounds) : pItem.bounds.clone();
      });
      targetCenter = compoundBounds ? compoundBounds.center : new paper.Point(0, 0);
    }

    selectedPaperObjects.forEach((pItem) => {
      pItem.scale(scaleFactor, targetCenter);
    });

    triggerCanvasUpdate();
  }, [isEnabled, selectedPaperObjects, getLargestItemInfo, triggerCanvasUpdate]);

  useEffect(() => {
    if (isEnabled && currentMaxSize > 0) {
      setMaxSize(currentMaxSize.toFixed(2));
    } else {
      setMaxSize('');
    }
  }, [selectedPaperObjects, currentMaxSize, isEnabled]);

  useEffect(() => {
    const sliderEl = sliderRef.current;
    if (!sliderEl) return;

    const handleWheel = (e) => {
      if (!isEnabled || currentMaxSize <= 0) return;
      e.preventDefault();

      const step = e.shiftKey ? 0.1 : 0.01;
      const delta = e.deltaY < 0 ? step : -step;
      const newSize = Math.max(0.01, currentMaxSize + delta);

      scaleAllToTarget(newSize);
    };

    sliderEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      sliderEl.removeEventListener('wheel', handleWheel);
    };
  }, [isEnabled, currentMaxSize, scaleAllToTarget]);

  const handleStepSize = (deltaMm = 0.01) => {
    if (!isEnabled) return;
    const { sizeMm } = getLargestItemInfo();
    if (sizeMm <= 0) return;

    const newSize = Math.max(0.01, sizeMm + deltaMm);
    scaleAllToTarget(newSize);
  };

  const stopRepeat = () => {
    if (repeatDelayRef.current) clearTimeout(repeatDelayRef.current);
    if (repeatTimerRef.current) clearInterval(repeatTimerRef.current);
  };

  const startRepeat = (deltaMm) => {
    if (!isEnabled) return;
    stopRepeat();

    handleStepSize(deltaMm);

    repeatDelayRef.current = setTimeout(() => {
      repeatTimerRef.current = setInterval(() => {
        handleStepSize(deltaMm);
      }, 50);
    }, 250);
  };

  useEffect(() => {
    return () => stopRepeat();
  }, []);

  const handleKeyDown = (e) => {
    if (!isEnabled) return;

    const isPlus = e.key === 'ArrowUp' || e.key === '+' || e.key === '=';
    const isMinus = e.key === 'ArrowDown' || e.key === '-';

    if (isPlus || isMinus) {
      e.preventDefault();

      const step = e.shiftKey ? 0.1 : 0.01;
      handleStepSize(isPlus ? step : -step);
    }
  };

  const handleCenterAll = () => {
    if (!isEnabled || !selectedPaperObjects.length) return;

    const glassCenter = new paper.Point(0, 0);

    if (selectedPaperObjects.length === 1) {
      const item = selectedPaperObjects[0];
      const info = calculateMMBounds(item);
      const currentCenter = info?.geoCenter
        ? new paper.Point(info.geoCenter.x, info.geoCenter.y)
        : item.bounds.center;

      const delta = glassCenter.subtract(currentCenter);
      item.position = item.position.add(delta);
    } else {
      let compoundBounds = null;
      selectedPaperObjects.forEach((pItem) => {
        compoundBounds = compoundBounds ? compoundBounds.unite(pItem.bounds) : pItem.bounds.clone();
      });

      if (compoundBounds) {
        const delta = glassCenter.subtract(compoundBounds.center);
        selectedPaperObjects.forEach((pItem) => {
          pItem.position = pItem.position.add(delta);
        });
      }
    }

    triggerCanvasUpdate();
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setMaxSize(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      scaleAllToTarget(num);
    }
  };

  return (
    <div
      className={`border rounded-md p-2 flex flex-col gap-2 transition-all duration-300 ${
        theme === 'dark' ? 'bg-zinc-950/70 border-zinc-800' : 'bg-white border-slate-100 shadow-sm'
      } ${
        isEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none select-none'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-[11px] font-semibold uppercase flex items-center gap-1 ${
          theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'
        }`}>
          <Maximize2 className="w-3.5 h-3.5 text-indigo-400"/> {t('transform.quick_size')}
        </span>
        {!isEnabled && (
          <span className="text-[10px] text-neutral-500 italic">{t('transform.select_object_hint')}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={handleCenterAll}
          disabled={!isEnabled}
          title={t('transform.center_object_title')}
          className={`p-1.5 rounded transition-colors flex items-center justify-center border ${
            theme === 'dark' 
              ? 'bg-zinc-800 hover:bg-zinc-700 active:bg-indigo-600 text-zinc-200 hover:text-white border-zinc-700' 
              : 'bg-slate-100 hover:bg-slate-200 active:bg-indigo-600 text-slate-700 hover:text-slate-900 border-slate-200'
          }`}
        >
          <Crosshair className="w-4 h-4"/>
        </button>


        <button
          type="button"
          onClick={() => scaleAllToTarget(MAX_SMALL_GLASS_SIZE)}
          disabled={!isEnabled}
          className={`px-2 py-1 text-xs font-medium rounded border transition-colors cursor-pointer ${
            is26Active
              ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm shadow-indigo-500/50'
              : theme === 'dark'
                ? 'bg-zinc-800 hover:bg-indigo-600 text-zinc-200 hover:text-white border-zinc-700'
                : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
          }`}
        >
          {MAX_SMALL_GLASS_SIZE} мм
        </button>

        <button
          type="button"
          onClick={() => scaleAllToTarget(MAX_LARGE_GLASS_SIZE)}
          disabled={!isEnabled}
          className={`px-2 py-1 text-xs font-medium rounded border transition-colors cursor-pointer ${
            is46Active
              ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm shadow-indigo-500/50'
              : theme === 'dark'
                ? 'bg-zinc-800 hover:bg-indigo-600 text-zinc-200 hover:text-white border-zinc-700'
                : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
          }`}
        >
          {MAX_LARGE_GLASS_SIZE} мм
        </button>


        <div className="flex items-center gap-1 ml-auto">
          <div className={`flex items-center border rounded overflow-hidden ${
            theme === 'dark' ? 'border-zinc-700 bg-zinc-900' : 'border-slate-200 bg-white'
          }`}>

            <button
              type="button"
              onMouseDown={() => startRepeat(-0.01)}
              onMouseUp={stopRepeat}
              onMouseLeave={stopRepeat}
              onTouchStart={() => startRepeat(-0.01)}
              onTouchEnd={stopRepeat}
              disabled={!isEnabled}
              title={t('sidebar.text_settings.size_decrease')}
              className={`p-1.5 transition-colors border-r flex items-center justify-center select-none ${
                theme === 'dark'
                  ? 'bg-zinc-800 hover:bg-zinc-700 active:bg-indigo-600 text-zinc-300 hover:text-white border-zinc-700'
                  : 'bg-slate-50 hover:bg-slate-100 active:bg-indigo-600 text-slate-600 hover:text-slate-900 border-slate-200'
              }`}
            >
              <Minus className="w-3 h-3" />
            </button>

            <input
              type="number"
              step="0.01"
              min="0.01"
              value={maxSize}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={!isEnabled}
              placeholder="0.00"
              className={`w-16 bg-transparent px-1 py-0.5 text-xs text-center outline-none font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                theme === 'dark' ? 'text-indigo-300' : 'text-indigo-600'
              }`}
            />


            <button
              type="button"
              onMouseDown={() => startRepeat(0.01)}
              onMouseUp={stopRepeat}
              onMouseLeave={stopRepeat}
              onTouchStart={() => startRepeat(0.01)}
              onTouchEnd={stopRepeat}
              disabled={!isEnabled}
              title={t('sidebar.text_settings.size_increase')}
              className={`p-1.5 transition-colors border-l flex items-center justify-center select-none ${
                theme === 'dark'
                  ? 'bg-zinc-800 hover:bg-zinc-700 active:bg-indigo-600 text-zinc-300 hover:text-white border-zinc-700'
                  : 'bg-slate-50 hover:bg-slate-100 active:bg-indigo-600 text-slate-600 hover:text-slate-900 border-slate-200'
              }`}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        </div>
        <span className={`text-[11px] ${theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'}`}></span>
      </div>


      <div className="flex flex-col gap-0.5 pt-0.5" ref={sliderRef}>
        <div className="flex justify-between text-[10px] text-zinc-500">
          <span>{t('transform.scroll_hint')}</span>
          <span className={`font-mono ${theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'}`}>
            {currentMaxSize > 0 ? `${currentMaxSize.toFixed(2)} ${t('layers.mm')}` : '-'}
          </span>
        </div>
        <input
          type="range"
          min="5"
          max="200"
          step="0.01"
          value={currentMaxSize || 5}
          onChange={(e) => scaleAllToTarget(parseFloat(e.target.value))}
          disabled={!isEnabled}
          className={`w-full h-1.5 rounded cursor-pointer accent-indigo-500 ${
            theme === 'dark' ? 'bg-zinc-800' : 'bg-slate-100'
          }`}
        />
      </div>
    </div>
  );
}